import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo from "@/helpers/apollo";
import { PAGE_SIZE } from "@/pages/user/games";
import { QUERY } from "@/pages/user/games/graphql";
import {
  GameSortOptionsEnum,
  type GamesDataType,
  type GamesVariablesType,
} from "@/pages/user/types";

interface ExperiencesInput {
  category?: string;
  sortBy?: string;
  subcategory?: string;
  username: string;
}

interface WishlistInput {
  category?: string;
  subcategory?: string;
  username: string;
}

async function fetchUserGames(
  username: string,
  gameVariables: Record<string, unknown>,
  userReview = false
) {
  const client = apollo();
  const variables = {
    ...gameVariables,
    isSSR: true,
    isUser: false,
    pageSize: PAGE_SIZE,
    username,
    userReview,
  } as GamesVariablesType;

  const response = await client.query<GamesDataType, GamesVariablesType>({
    fetchPolicy: "no-cache",
    query: QUERY,
    variables,
  });

  if (!response.data) {
    throw notFound();
  }

  return { ...response.data, variables };
}

export const fetchExperiences = createServerFn({ method: "GET" })
  .inputValidator((data: ExperiencesInput) => data)
  .handler(async ({ data }) => {
    let sortBy: GameSortOptionsEnum =
      (data.sortBy as GameSortOptionsEnum) || GameSortOptionsEnum.DATE_PLAYED;

    if (sortBy === GameSortOptionsEnum.MY_RATING) {
      sortBy = GameSortOptionsEnum.DATE_PLAYED;
    }

    const variables: Record<string, unknown> = { escapedOnly: true, sortBy };

    if (data.category) {
      let filterCategory = data.category;
      if (data.subcategory) {
        filterCategory += `/${data.subcategory}`;
      }
      variables.filters = { categorySlugs: [filterCategory] };
    }

    return await fetchUserGames(
      data.username,
      variables,
      sortBy === GameSortOptionsEnum.USER_RATING
    );
  });

export const fetchWishlist = createServerFn({ method: "GET" })
  .inputValidator((data: WishlistInput) => data)
  .handler(async ({ data }) => {
    const variables: Record<string, unknown> = { savedOnly: true };

    if (data.category) {
      let filterCategory = data.category;
      if (data.subcategory) {
        filterCategory += `/${data.subcategory}`;
      }
      variables.filters = { categorySlugs: [filterCategory] };
    }

    return await fetchUserGames(data.username, variables);
  });
